import {isPreviewMode} from "utils/previewMode";
// ---------------------------------------------------------------------
// Quick-change preview SPA-fallback guard.
//
// CloudFront's SPA fallback on the staging distribution returns the
// LIVE shell's `/index.html` for any request that misses an S3 object.
// That means a hit to `https://staging.console.workloads.netapp.com/previews/pr-<n>/`
// (no `index.html`) — which happens whenever the browser is refreshed
// on a preview URL, or whenever Auth0 returns to the origin root and
// the live shell tries to navigate back into the preview — serves the
// LIVE bundle from the preview URL. The live bundle then runs the
// catch-all <Navigate to="/" replace /> and dumps the user on live
// staging, or worse, loops via the auth middleware's preview-detect
// hard-nav.
//
// VITE_PREVIEW_BASE is set at build time only by the preview workflow,
// so its absence here means we're the live bundle. If we're the live
// bundle AND the URL is a preview path, hard-redirect to the preview
// shell's `/index.html` (which IS an S3 object) so the right bundle
// runs.
//
// Doing this BEFORE any other imports keeps the wrong-bundle work
// (store creation, router setup, network calls) to an absolute minimum.
const _previewSpaFallbackRedirect = (() => {
    if (!isPreviewMode()) return false;
    if (import.meta.env.VITE_PREVIEW_BASE) return false; // already preview bundle
    const m = window.location.pathname.match(/^(\/previews\/pr-\d+\/)/);
    if (!m) return false;
    if (window.location.pathname.endsWith('/index.html')) return false;
    const target = m[1] + 'index.html' + window.location.search + window.location.hash;
    window.location.replace(target);
    return true;
})();

import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import store,{history} from 'store';
import {Provider} from "react-redux";
import {
    unstable_HistoryRouter as HistoryRouter,
    Link,
    Route,
    Routes,
    NavLink
} from "react-router-dom";
import auth0 from "auth0-js";
import { initModalTargetListener, setInnerTabLinkProvider } from "@netapp/bxp-design-system-react";
import "./modules/app/configureYup"
import { ThemeContextController } from "modules/app/ThemeContext";

const isIntegratedMFE = import.meta.env.VITE_IS_APP_INTEGRATED_MFE;
const isIframe = window.top !== window;
const loadedUnderBXP = isIntegratedMFE || isIframe; // remove iframe once shell is fully integrated as shadow dom

setInnerTabLinkProvider({ Link, NavLink });
// Skip boot when the SPA-fallback guard fired — the page is mid-navigation
// to the preview shell's index.html and dispatching APP/BOOT here would
// kick off API calls + auth flows in the wrong (live) bundle that the
// next page load will immediately throw away.
if (!_previewSpaFallbackRedirect) {
    if (!loadedUnderBXP) {
        window.auth0 = auth0;

        store.dispatch({
            type: "APP/BOOT"
        });
    } else {
        store.dispatch({
            type: "APP/MINI-BOOT"
        });
    }
}

history.listen(() => {
    window.scrollTo(0, 0);
});

initModalTargetListener();

const RedirectApiDoc = () => {
    window.location.replace(`${window.location.origin}/api-doc#pathname=${window.location.pathname.replace("/api-doc", "")}`);
    return <div/>
}

const RedirectStyleGuide = () => {
    window.location.replace(`${window.location.origin}/styleguide/index.html?path=${window.location.pathname}`)
    return <div/>
}

const FSXCoreApp = (props?: any) => {
	return (
		<Provider store={store}>
			<HistoryRouter history={history}>
				<ThemeContextController>
					<Routes>
						<Route path="/api-doc/*" element={<RedirectApiDoc />} />
						<Route path="/styleguide/*" element={<RedirectStyleGuide />} />
						<Route path="*" element={<App {...props} />} />
					</Routes>
				</ThemeContextController>
			</HistoryRouter>
		</Provider>
	);
};

if (!isIntegratedMFE && !_previewSpaFallbackRedirect) {
	import("@netapp/bxp-style/css/reset.css");
	import("@netapp/bxp-design-system-react/dist/index.css");
	ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
		<React.StrictMode>
			<FSXCoreApp />
		</React.StrictMode>
	);
}

export default FSXCoreApp;
